import * as React from 'react';
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { format } from 'date-fns';

import YadoSearchFrom from './YadoSearchForm';
import ItemSearchForm from './ItemSearchForm';
import { ISearchFormProps } from './types';
import Field from './Field';
import PillNavs from './PillNavs';
import { SNOW_SEARCH_MODAL_TRIGGERED } from './events';
import { countNights, countPeople } from './helpers';
import { parseDate, dateFnsLocale} from '../utils/dateHelper';
import { getLocale } from '../utils/pathHelper';
import t from './locales';

const ModalDesktop = styled.div`
  transition: all 0.2s ease-out;
  top: -500vh;
  background-color: var(--blue100);
  width: 680px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.2);
  position: absolute;
  margin-left: auto;
  margin-right: auto;
  left: 0;
  right: 0;
  z-index: 1090;
  &.show {
    top: 56px;
  }
  .snow-search-modal-body {
    padding: 2em;
    display: flex;
    justify-content: center;
  }
`;

const ModalMobile = styled.div`
  transition: all 0.2s ease-out;
  background-color: var(--blue100);
  top: 100vh;
  right: 0;
  bottom: 0;
  left: 0;
  position: fixed;
  height: 100vh;
  z-index: 1090;
  &.show {
    top: 0
  }
  .snow-search-modal-body {
    margin-top: 1em;
  }
`;

const Header = styled.div`
  background-color: var(--gray0);
  padding: 1em 1.5em;
  display: flex;
  justify-content: space-between;
  align-items: center;
  p {
    margin: 0;
    font-weight: bold;
  }
  i {
    font-size: 2em;
  }
`;

const IconView = styled.div`
  --size: 40px;
  cursor: pointer;
  width: var(--size);
  height: var(--size);
  font-size: 2.25em;
  text-align: center;
  line-height: var(--size);
  @media (max-width: 480px) {
    --size: 32px;
    font-size: 2em;
  }
`;

const PillNavsContainer = styled.div`
  overflow-x: unset;
  margin-bottom: 1em;
  @media (max-width: 480px) {
    padding: 0 0 1em 1em;
    margin: 0 0 1em 1em;
    overflow-x: scroll;
  }
`;

interface IProps extends ISearchFormProps {
  triggerType: 'yadoField'|'icon'
  recommended_stay_list?: any;
  snow_item_candidate_list?: any;
  isMobile: boolean;
  activePill: string;
}

const YadoSearchFormModal: React.FC<IProps> = (props) => {
  const { checkin_date, checkout_date, rooms, q, triggerType, isMobile, activePill } = props;
  const [isShown, setShown] = useState<boolean>(false);
  const body = document.getElementsByTagName('body')[0];
  const locale = getLocale({ underscore: true });

  function handleClick(event: React.MouseEvent): void {
    event.preventDefault();
    setShown(true);
    if (body) { body.style.overflow = 'hidden'; }
  }

  function handleCloseClick(event: React.MouseEvent): void {
    event.preventDefault();
    setShown(false);
    if (body) { body.style.overflow = 'inherit'; }
  }

  function handleModalTriggered() {
    setShown(true);
    if (body) { body.style.overflow = 'hidden'; }
  }

  let subTexts = [];
  const parsedDate = parseDate(checkin_date);
  if (parsedDate) {
    const shortDate = [
      format(parsedDate, 'LLL', { locale: dateFnsLocale() }),
      format(parsedDate, 'do', { locale: dateFnsLocale() }),
    ].join(' ');
    const numberOfNights = countNights(checkin_date, checkout_date);
    const numberOfPeople = countPeople(rooms);
    subTexts = [
      `${shortDate}〜`,
      (numberOfNights && numberOfPeople && t.guestAndNightCount({ nights: numberOfNights, guests: numberOfPeople.total }))
    ];
  }

  useEffect(() => {
    if (triggerType === 'yadoField') {
      document.addEventListener(SNOW_SEARCH_MODAL_TRIGGERED, handleModalTriggered);
      return () => {
        document.removeEventListener(SNOW_SEARCH_MODAL_TRIGGERED, handleModalTriggered);
      };
    }
  }, []);

  function renderTrigger() {
    if (triggerType === 'icon') {
      // global-site-nav__btn d-block mx-1
      return (
        <IconView onClick={handleClick} className="menuBtn">
          <i className="menuBtnIcon icon-search" />
        </IconView>
      );
    } else {
      return (
        <Field
          icon="search"
          readOnly
          placeholder=""
          label={{ main: (q && q.label), sub: subTexts.filter(text => !!text).join('・') }}
          handleClick={handleClick} />
      );
    }
  }

  const ModalView = isMobile ? ModalMobile : ModalDesktop;

  if (locale == 'ja') {
    return (
      <React.Fragment>
        {renderTrigger()}
        <ModalView className={isShown ? 'show' : null}>
          <Header>
            <p>{t.searchTitle({ key: activePill })}</p>
            <i className="icon-cancel" onClick={handleCloseClick} />
          </Header>
          <div className="snow-search-modal-body">
            <div className="mx-3">
              <ItemSearchForm
                q={null}
                isMobile={isMobile}
                snow_item_candidate_list={props.snow_item_candidate_list || {}}
                is_toppage={false}
              />
            </div>
          </div>
        </ModalView>
      </React.Fragment>
    );  
  }
  else {
    return (
      <React.Fragment>
        {renderTrigger()}
        <ModalView className={isShown ? 'show' : null}>
          <Header>
            <p>{t.searchTitle({ key: activePill })}</p>
            <i className="icon-cancel" onClick={handleCloseClick} />
          </Header>
          <div className="snow-search-modal-body">
            <div>
              <PillNavsContainer>
                <PillNavs navArray={{
                  yado: ['yado'],
                  item: ['lift', 'rental', 'lesson'],
                  insurance: ['insurance']
                }} q={props.q} />
              </PillNavsContainer>
              {activePill === 'yado' &&
                <YadoSearchFrom {...props} placement="modal" isMobile={isMobile} is_toppage={false} />}
              {activePill === 'item' &&
                <ItemSearchForm
                  q={null}
                  isMobile={isMobile}
                  snow_item_candidate_list={props.snow_item_candidate_list || {}}
                  is_toppage={false}
                />}
            </div>
          </div>
        </ModalView>
      </React.Fragment>
    );
  }
}

export default YadoSearchFormModal;
